import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerLinkSx, partnerOutlineBtnSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { UNIT_TYPES } from '../../utils/groupBilling';
import { SectionCard, eyebrowSx, fieldSx } from './BillingParts';

const emptyUnit = { label: '', unitType: 'hotel_room', category: '', capacity: 2, startDate: '', endDate: '', notes: '' };

export function GroupUnitDialog({ open, onClose, unit, onSave, saving = false, error = '' }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyUnit);
  useEffect(() => {
    if (!open) return;
    setForm(unit ? {
      label: unit.label || '',
      unitType: unit.unitType || 'hotel_room',
      category: unit.category || '',
      capacity: unit.capacity || 2,
      startDate: unit.startDate || '',
      endDate: unit.endDate || '',
      notes: unit.notes || '',
    } : emptyUnit);
  }, [open, unit]);
  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="group-unit-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
        {unit ? t('groupBilling.rooms.editTitle') : t('groupBilling.rooms.addTitle')}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl size="small" sx={fieldSx}>
              <InputLabel>{t('groupBilling.rooms.type')}</InputLabel>
              <Select label={t('groupBilling.rooms.type')} value={form.unitType} onChange={(e) => patch({ unitType: e.target.value })}>
                {UNIT_TYPES.map((u) => <MenuItem key={u} value={u}>{t(`groupBilling.unitType.${u}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label={t('groupBilling.rooms.label')}
              size="small"
              sx={fieldSx}
              value={form.label}
              onChange={(e) => patch({ label: e.target.value })}
              placeholder={t('groupBilling.rooms.labelPlaceholder')}
              inputProps={{ 'data-testid': 'group-unit-label' }}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
            <TextField label={t('groupBilling.rooms.category')} size="small" sx={fieldSx}
              value={form.category} onChange={(e) => patch({ category: e.target.value })}
              placeholder={t('groupBilling.rooms.categoryPlaceholder')} />
            <TextField label={t('groupBilling.rooms.capacity')} type="number" size="small" sx={fieldSx}
              value={form.capacity} onChange={(e) => patch({ capacity: e.target.value })} inputProps={{ min: 1, max: 12 }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField label={t('groupBilling.rooms.checkIn')} type="date" size="small" sx={fieldSx} InputLabelProps={{ shrink: true }}
              value={form.startDate} onChange={(e) => patch({ startDate: e.target.value })} />
            <TextField label={t('groupBilling.rooms.checkOut')} type="date" size="small" sx={fieldSx} InputLabelProps={{ shrink: true }}
              value={form.endDate} onChange={(e) => patch({ endDate: e.target.value })} />
          </Box>
          <TextField label={t('groupBilling.rooms.notes')} size="small" sx={fieldSx} multiline minRows={2}
            value={form.notes} onChange={(e) => patch({ notes: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${DOCUMENT.rule}` }}>
        <Button onClick={onClose} sx={partnerLinkSx}>{t('common.cancel')}</Button>
        <Button
          onClick={() => onSave({ ...form, capacity: Number(form.capacity) || 2 })}
          disabled={saving || !form.label.trim()}
          sx={partnerOxbloodBtnSx}
          data-testid="group-unit-save"
        >
          {saving ? <CircularProgress size={20} sx={{ color: DOCUMENT.ivory }} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const GroupRoomsBoard = ({ units = [], travelers = [], charges = [], onAdd, onEdit, onDelete, onSetOccupants, busy = false }) => {
  const { t } = useTranslation();
  const [drag, setDrag] = useState(null);
  const [overUnit, setOverUnit] = useState(undefined);

  const placed = useMemo(() => {
    const set = new Set();
    units.forEach((u) => u.occupants.forEach((o) => set.add(o.travelerId)));
    return set;
  }, [units]);
  const unplaced = travelers.filter((tr) => !placed.has(tr.id));

  const sameTypeTaken = (unit) => {
    const set = new Set();
    units.filter((u) => u.unitType === unit.unitType).forEach((u) => u.occupants.forEach((o) => set.add(o.travelerId)));
    return set;
  };

  const moveTo = async (travelerId, fromUnitId, toUnitId) => {
    if (fromUnitId === toUnitId) return;
    const dest = toUnitId ? units.find((u) => u.id === toUnitId) : null;
    if (dest && dest.occupants.some((o) => o.travelerId === travelerId)) return;
    if (fromUnitId) {
      const src = units.find((u) => u.id === fromUnitId);
      const ok = await onSetOccupants(fromUnitId, src.occupants.map((o) => o.travelerId).filter((id) => id !== travelerId));
      if (ok === false) return;
    }
    if (dest) {
      await onSetOccupants(toUnitId, [...dest.occupants.map((o) => o.travelerId), travelerId]);
    }
  };

  const chargeTotal = (unit) => charges
    .filter((c) => unit.chargeIds.includes(c.id))
    .reduce((s, c) => s + Number(c.total || 0), 0);

  const dropProps = (unitId) => ({
    onDragOver: (e) => { e.preventDefault(); setOverUnit(unitId); },
    onDragLeave: () => setOverUnit(undefined),
    onDrop: (e) => {
      e.preventDefault();
      setOverUnit(undefined);
      if (drag) moveTo(drag.travelerId, drag.fromUnitId, unitId);
      setDrag(null);
    },
  });

  const travelerChip = (tr, fromUnitId, onDelete) => (
    <Chip
      key={tr.id}
      size="small"
      label={`${tr.name}${tr.travelerType && tr.travelerType !== 'adult' ? ` · ${t(`groupBilling.travelerType.${tr.travelerType}`)}` : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(tr.id));
        setDrag({ travelerId: tr.id, fromUnitId });
      }}
      onDelete={onDelete}
      data-testid={fromUnitId ? `group-room-${fromUnitId}-traveler-${tr.id}` : `group-room-traveler-${tr.id}`}
      sx={{ borderRadius: 0, cursor: 'grab', bgcolor: '#fff', border: `1px solid ${DOCUMENT.ruleHard}` }}
    />
  );

  return (
    <SectionCard
      title={t('groupBilling.rooms.title')}
      testId="group-rooms-board"
      action={(
        <Button sx={partnerOutlineBtnSx} onClick={onAdd} data-testid="group-room-add">
          {t('groupBilling.rooms.add')}
        </Button>
      )}
    >
      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>{t('groupBilling.rooms.hint')}</Typography>
      <Box
        {...dropProps(null)}
        sx={{ border: `1px dashed ${overUnit === null && drag ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`, p: 1.5, mb: 2 }}
        data-testid="group-rooms-unassigned"
      >
        <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.rooms.unassigned', { count: unplaced.length })}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {unplaced.map((tr) => travelerChip(tr, null))}
          {!unplaced.length && <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('groupBilling.rooms.allPlaced')}</Typography>}
        </Box>
      </Box>
      {!units.length && (
        <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink3 }}>{t('groupBilling.rooms.empty')}</Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        {units.map((unit) => {
          const full = unit.occupants.length >= unit.capacity;
          const total = chargeTotal(unit);
          const taken = sameTypeTaken(unit);
          const options = travelers.filter((tr) => !taken.has(tr.id));
          return (
            <Box
              key={unit.id}
              {...dropProps(unit.id)}
              data-testid={`group-room-${unit.id}`}
              sx={{
                border: `1px solid ${overUnit === unit.id ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
                bgcolor: '#fff',
                p: 1.5,
                opacity: busy ? 0.7 : 1,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 17 }}>{unit.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                    {[t(`groupBilling.unitType.${unit.unitType}`), unit.category].filter(Boolean).join(' · ')}
                    {' · '}
                    {t('groupBilling.rooms.occupancy', { count: unit.occupants.length, capacity: unit.capacity })}
                  </Typography>
                </Box>
                <Box>
                  <IconButton size="small" aria-label={t('common.edit')} onClick={() => onEdit(unit)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label={t('common.delete')} onClick={() => onDelete(unit)} data-testid={`group-room-delete-${unit.id}`}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1.25, minHeight: 28 }}>
                {unit.occupants.map((o) => {
                  const tr = travelers.find((x) => x.id === o.travelerId) || { id: o.travelerId, name: o.name, travelerType: o.travelerType };
                  return travelerChip(tr, unit.id, () => moveTo(o.travelerId, unit.id, null));
                })}
              </Box>
              {!full && options.length > 0 && (
                <Select
                  size="small"
                  displayEmpty
                  value=""
                  onChange={(e) => moveTo(Number(e.target.value), null, unit.id)}
                  sx={{ borderRadius: 0, fontSize: 13, width: '100%' }}
                  inputProps={{ 'data-testid': `group-room-assign-${unit.id}` }}
                >
                  <MenuItem value="" disabled>{t('groupBilling.rooms.assign')}</MenuItem>
                  {options.map((tr) => <MenuItem key={tr.id} value={tr.id}>{tr.name}</MenuItem>)}
                </Select>
              )}
              {unit.chargeIds.length > 0 && (
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink2, mt: 1 }}>
                  {t('groupBilling.rooms.linkedCharges', { count: unit.chargeIds.length, total: total.toLocaleString() })}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
};

export default GroupRoomsBoard;
